const SHOW_SOLD_OUT_PARAM = 'show_sold_out';
const AVAILABILITY_CHOICE_PARAM = 'availability_choice';
const AVAILABILITY_STATES = ['in-stock', 'sold-out', 'all'];
const LOCATION_COUNT_REQUESTS = new Map();

class SoldOutFilter extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.controller = new AbortController();
    this.filterName = this.dataset.filterName || 'filter.v.availability';
    this.inStockValue = this.dataset.inStockValue || '1';
    this.soldOutValue = this.dataset.soldOutValue || '0';
    this.locationFilterName = this.dataset.locationFilterName || '';

    document.addEventListener(
      'section:content-updated',
      this.handleSectionUpdate.bind(this),
      { signal: this.controller.signal }
    );
    window.addEventListener('popstate', this.handleSectionUpdate.bind(this), {
      signal: this.controller.signal,
    });
    this.addEventListener('change', this.handleChange.bind(this), {
      signal: this.controller.signal,
    });

    this.syncState();
    void this.refreshLocationCounts();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  getUrl() {
    return new URL(window.location.href);
  }

  isShowingSoldOut(url) {
    return url.searchParams.get(SHOW_SOLD_OUT_PARAM) === '1';
  }

  getState(url) {
    const choice = url.searchParams.get(AVAILABILITY_CHOICE_PARAM);
    if (AVAILABILITY_STATES.includes(choice)) return choice;

    const values = url.searchParams.getAll(this.filterName);
    if (values.includes(this.soldOutValue)) return 'sold-out';
    if (this.isShowingSoldOut(url)) return 'all';
    return 'in-stock';
  }

  cacheElements() {
    this.select = this.querySelector('[data-availability-select]');
    this.inStockInput = this.querySelector(
      '[data-availability-in-stock-input]'
    );
    this.soldOutInput = this.querySelector(
      '[data-availability-sold-out-input]'
    );
    this.allInput = this.querySelector('[data-availability-all-input]');
    this.locations = this.querySelector('[data-availability-locations]');
    this.locationInputs = Array.from(
      this.querySelectorAll('[data-stock-location-input]')
    );
  }

  valueForState(input, state) {
    if (state === 'sold-out') return input.dataset.soldOutValue || '';
    if (state === 'all') return input.dataset.allValue || '';
    return input.dataset.inStockValue || '';
  }

  syncLocationSelectionsFromUrl(url, state) {
    if (!this.locationFilterName) return;

    const values = url.searchParams.getAll(this.locationFilterName);
    this.locationInputs.forEach((input) => {
      input.checked = values.includes(this.valueForState(input, state));
    });
  }

  locationNameFromValue(value) {
    return String(value || '').replace(/::(?:sold-out|all)$/, '');
  }

  ensureUrlLocationInputs(url) {
    if (!this.locationFilterName || !this.locations) return;

    const list = this.locations.querySelector('ul');
    if (!list) return;

    const existingNames = new Set(
      this.locationInputs.map((input) => input.dataset.locationName || '')
    );
    const showCounts = Boolean(
      this.locations.querySelector('[data-stock-location-count]')
    );

    url.searchParams.getAll(this.locationFilterName).forEach((value) => {
      const locationName = this.locationNameFromValue(value);
      if (!locationName || existingNames.has(locationName)) return;

      const locationId = `${this.closest('smooth-collapse')?.id || 'ShopAvailability'}-url-${locationName}`
        .replace(/[^a-z0-9_-]+/gi, '-')
        .replace(/-+/g, '-');
      const item = document.createElement('li');
      const checkbox = document.createElement('div');
      const input = document.createElement('input');
      const label = document.createElement('label');
      const labelText = document.createElement('span');
      const name = document.createElement('span');
      const boxTemplate = this.locations.querySelector('.checkbox__box');
      const box = boxTemplate?.cloneNode(true) || document.createElement('span');

      item.dataset.urlSelectedLocation = '';
      checkbox.className = 'checkbox checkbox--filter';
      input.type = 'checkbox';
      input.id = locationId;
      input.name = this.locationFilterName;
      input.checked = true;
      input.dataset.stockLocationInput = '';
      input.dataset.locationName = locationName;
      input.dataset.inStockValue = locationName;
      input.dataset.soldOutValue = `${locationName}::sold-out`;
      input.dataset.allValue = `${locationName}::all`;
      label.className = 'checkbox__label';
      label.htmlFor = locationId;
      box.className = 'checkbox__box';
      labelText.className = 'checkbox__label-text';
      name.textContent = locationName;
      labelText.append(name);

      if (showCounts) {
        const count = document.createElement('span');
        count.className = 'filter__active-count';
        count.setAttribute('aria-hidden', 'true');
        count.dataset.stockLocationCount = '';
        count.dataset.inStockCount = '0';
        count.dataset.soldOutCount = '0';
        count.dataset.allCount = '0';
        count.textContent = '(0)';
        labelText.append(count);
      }

      label.append(box, labelText);
      checkbox.append(input, label);
      item.append(checkbox);
      list.append(item);
      existingNames.add(locationName);
    });
  }

  selectedLocationNames() {
    return this.locationInputs
      .filter((input) => input.checked)
      .map((input) => input.dataset.locationName || '')
      .filter(Boolean);
  }

  syncLocationSelections(names) {
    const selected = new Set(names);
    this.locationInputs.forEach((input) => {
      input.checked = selected.has(input.dataset.locationName || '');
    });
  }

  updateLocationInputs(state) {
    if (!this.locationFilterName) return;

    this.locationInputs.forEach((input) => {
      input.name = this.locationFilterName;
      input.value = this.valueForState(input, state);
    });
  }

  updateLocationCounts(state) {
    this.locationInputs.forEach((input) => {
      const count = input
        .closest('.checkbox')
        ?.querySelector('[data-stock-location-count]');
      if (!count) return;

      count.textContent = `(${this.locationCountForState(input, state)})`;
    });
  }

  locationCountSourceUrl() {
    const url = this.getUrl();
    const sectionId = this.closest('[id^="shopify-section-"]')?.id.replace(
      /^shopify-section-/,
      ''
    );

    url.searchParams.delete(this.filterName);
    url.searchParams.delete(this.locationFilterName);
    url.searchParams.delete(SHOW_SOLD_OUT_PARAM);
    url.searchParams.delete(AVAILABILITY_CHOICE_PARAM);
    url.searchParams.delete('page');

    if (sectionId) url.searchParams.set('section_id', sectionId);

    return url;
  }

  parseLocationCounts(html) {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const counts = new Map();

    document.querySelectorAll('[data-stock-location-input]').forEach((input) => {
      const filter = input.closest('sold-out-filter');
      const count = input
        .closest('.checkbox')
        ?.querySelector('[data-stock-location-count]');
      const locationName = input.dataset.locationName || '';

      if (
        filter?.dataset.locationFilterName !== this.locationFilterName ||
        !locationName ||
        !count ||
        counts.has(locationName)
      ) {
        return;
      }

      counts.set(locationName, {
        all: count.dataset.allCount || '0',
        inStock: count.dataset.inStockCount || '0',
        soldOut: count.dataset.soldOutCount || '0',
      });
    });

    return counts;
  }

  applyLocationCountSnapshot(counts) {
    this.locationInputs.forEach((input) => {
      const locationName = input.dataset.locationName || '';
      const values = counts.get(locationName);
      const count = input
        .closest('.checkbox')
        ?.querySelector('[data-stock-location-count]');
      if (!values || !count) return;

      count.dataset.allCount = values.all;
      count.dataset.inStockCount = values.inStock;
      count.dataset.soldOutCount = values.soldOut;
    });

    const state = this.select?.value || this.getState(this.getUrl());
    this.updateLocationCounts(state);
    this.updateLocationVisibility(state);
  }

  async refreshLocationCounts() {
    if (!this.locationFilterName || this.locationInputs.length === 0) return;

    const url = this.locationCountSourceUrl();
    const cacheKey = url.toString();
    let request = LOCATION_COUNT_REQUESTS.get(cacheKey);

    if (!request) {
      request = fetch(cacheKey, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => this.parseLocationCounts(html))
        .finally(() => LOCATION_COUNT_REQUESTS.delete(cacheKey));
      LOCATION_COUNT_REQUESTS.set(cacheKey, request);
    }

    try {
      const counts = await request;
      if (this.isConnected) this.applyLocationCountSnapshot(counts);
    } catch (error) {}
  }

  locationCountForState(input, state) {
    const count = input
      .closest('.checkbox')
      ?.querySelector('[data-stock-location-count]');
    const value =
      state === 'sold-out'
        ? count?.dataset.soldOutCount
        : state === 'all'
          ? count?.dataset.allCount
          : count?.dataset.inStockCount;
    const parsed = Number.parseInt(value || '0', 10);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  updateLocationVisibility(state) {
    this.locationInputs.forEach((input) => {
      const item = input.closest('li');
      const shouldHide =
        this.locationCountForState(input, state) === 0 && !input.checked;

      item?.toggleAttribute('hidden', shouldHide);
      input.disabled = shouldHide;
    });
  }

  syncState() {
    this.cacheElements();
    const url = this.getUrl();
    const state = this.getState(url);

    this.ensureUrlLocationInputs(url);
    this.cacheElements();
    this.syncLocationSelectionsFromUrl(url, state);
    this.applyState(state);
  }

  applyState(state) {
    this.cacheElements();
    const hasSelectedLocations = this.selectedLocationNames().length > 0;

    if (this.select) this.select.value = state;
    if (this.inStockInput) {
      // A selected shop already encodes the requested availability state in
      // custom.in_stock_locations. Adding Shopify's native availability filter
      // would AND two independently indexed filters and can expose stale counts.
      this.inStockInput.disabled = state !== 'in-stock' || hasSelectedLocations;
    }
    if (this.soldOutInput) {
      this.soldOutInput.disabled =
        state !== 'sold-out' || hasSelectedLocations;
    }
    if (this.allInput) {
      this.allInput.disabled =
        state !== 'all' && !(state === 'sold-out' && hasSelectedLocations);
    }

    if (this.locations) {
      this.locations.removeAttribute('data-disabled');
      this.locations.setAttribute('aria-disabled', 'false');
      this.locationInputs.forEach((input) => {
        input.disabled = false;
      });
    }

    this.updateLocationInputs(state);
    this.updateLocationCounts(state);
    this.updateLocationVisibility(state);
  }

  handleChange(event) {
    if (event.target instanceof HTMLSelectElement) {
      if (!event.target.matches('[data-availability-select]')) return;

      const state = event.target.value;
      if (!AVAILABILITY_STATES.includes(state)) return;

      const selectedLocations = this.selectedLocationNames();
      document.querySelectorAll('sold-out-filter').forEach((filter) => {
        filter.syncLocationSelections(selectedLocations);
        filter.applyState(state);
      });

      return;
    }

    if (
      !(event.target instanceof HTMLInputElement) ||
      !event.target.matches('[data-stock-location-input]')
    ) {
      return;
    }

    const locationName = event.target.dataset.locationName || '';
    document.querySelectorAll('sold-out-filter').forEach((filter) => {
      (filter.locationInputs || [])
        .filter((input) => input.dataset.locationName === locationName)
        .forEach((input) => {
          input.checked = event.target.checked;
        });
      filter.applyState(filter.select?.value || filter.getState(filter.getUrl()));
    });
  }

  handleSectionUpdate() {
    this.syncState();
    void this.refreshLocationCounts();
  }
}

if (!customElements.get('sold-out-filter')) {
  customElements.define('sold-out-filter', SoldOutFilter);
}
